import { bounds as boundsOps, type Bounds } from '@/domain/geometry';

/**
 * Print layout: paper size, orientation, and tiling a drawing across sheets.
 *
 * ## Why this is separate from `exportFrame`
 *
 * They answer different questions. `exportFrame` asks *how big is the drawing* — it fits the
 * page to the content, which is right for a picture dropped into a document. Printing asks
 * the opposite: *the page is this size, where does the drawing go* — and if the drawing does
 * not fit, it has to be scaled down or split across sheets. Folding both into one function
 * would mean a flag that reverses which of the two is derived from the other.
 *
 * ## Units
 *
 * PostScript points, 72 to the inch, because that is what a PDF page is measured in and what
 * every print dialogue means by "A4". Converting once, here, keeps millimetres out of the
 * rest of the codebase.
 */

export const PaperSize = {
  A4: 'a4',
  A3: 'a3',
  Letter: 'letter',
  Legal: 'legal',
  /** No paper: the page is whatever the drawing needs. */
  Fit: 'fit',
} as const;
export type PaperSize = (typeof PaperSize)[keyof typeof PaperSize];

export const Orientation = {
  Portrait: 'portrait',
  Landscape: 'landscape',
} as const;
export type Orientation = (typeof Orientation)[keyof typeof Orientation];

/** Paper in points, portrait. Landscape is the same numbers swapped. */
const PAPER: Readonly<Record<Exclude<PaperSize, 'fit'>, readonly [number, number]>> = {
  a4: [595.28, 841.89],
  a3: [841.89, 1190.55],
  letter: [612, 792],
  legal: [612, 1008],
};

export const DEFAULT_PRINT_MARGIN = 36; // half an inch

export interface PageSetup {
  readonly paper: PaperSize;
  readonly orientation: Orientation;
  /** Blank border on every sheet, in points. */
  readonly margin: number;
  /**
   * Shrink the drawing to one sheet rather than tiling it.
   *
   * The two answers to "it does not fit" — smaller, or more sheets — and neither is right
   * for everybody. A diagram for a worksheet wants one page; a scale drawing wants its scale
   * kept and the sheets taped together.
   */
  readonly fitToPage: boolean;
  /** Output units per world unit, before any fitting. */
  readonly scale: number;
}

export const DEFAULT_PAGE_SETUP: PageSetup = Object.freeze({
  paper: PaperSize.Fit,
  orientation: Orientation.Portrait,
  margin: DEFAULT_PRINT_MARGIN,
  fitToPage: true,
  scale: 1,
});

export interface PrintPage {
  /** The slice of the world this sheet shows. */
  readonly world: Bounds;
  /** Zero-based position in the tiling. */
  readonly column: number;
  readonly row: number;
}

export interface PrintLayout {
  readonly pages: readonly PrintPage[];
  /** Sheet size in points, including margins. */
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly margin: number;
  /** Output units per world unit, after any fitting. */
  readonly scale: number;
  readonly columns: number;
  readonly rows: number;
}

/** Sheet dimensions in points. */
export function paperSize(
  paper: Exclude<PaperSize, 'fit'>,
  orientation: Orientation
): readonly [number, number] {
  const [width, height] = PAPER[paper];
  return orientation === Orientation.Landscape ? [height, width] : [width, height];
}

/**
 * Works out the sheets a drawing prints onto.
 *
 * With `paper: 'fit'` the result is always one page sized to the content — the behaviour the
 * exporters had before printing existed, kept as an option rather than removed, because it
 * is the right answer for a figure going into a document.
 */
export function planPrint(content: Bounds, setup: PageSetup = DEFAULT_PAGE_SETUP): PrintLayout {
  const world = boundsOps.isEmpty(content) ? boundsOps.bounds(-200, -200, 200, 200) : content;
  const worldWidth = Math.max(1e-6, boundsOps.width(world));
  const worldHeight = Math.max(1e-6, boundsOps.height(world));

  if (setup.paper === PaperSize.Fit) {
    /*
     * No print margin here, deliberately. A fitted page is framed by `exportFrame`, which has
     * already left a margin around the content — adding a second one double-spaces the border
     * and, worse, changes the size of every existing export. The print margin belongs to
     * *paper*, which is what the branch below is about.
     */
    const scale = setup.scale;
    return {
      pages: [{ world, column: 0, row: 0 }],
      pageWidth: worldWidth * scale,
      pageHeight: worldHeight * scale,
      margin: 0,
      scale,
      columns: 1,
      rows: 1,
    };
  }

  const [pageWidth, pageHeight] = paperSize(setup.paper, setup.orientation);
  const printableWidth = Math.max(1, pageWidth - setup.margin * 2);
  const printableHeight = Math.max(1, pageHeight - setup.margin * 2);

  /*
   * Fitting shrinks but never enlarges. Blowing a small diagram up to fill A3 is not what
   * "fit to page" means to anybody, and it would turn a 2 pt stroke into a 12 pt one.
   */
  const scale = setup.fitToPage
    ? Math.min(setup.scale, printableWidth / worldWidth, printableHeight / worldHeight)
    : setup.scale;

  const columns = Math.max(1, Math.ceil((worldWidth * scale) / printableWidth - 1e-9));
  const rows = Math.max(1, Math.ceil((worldHeight * scale) / printableHeight - 1e-9));

  const sliceWidth = printableWidth / scale;
  const sliceHeight = printableHeight / scale;

  const pages: PrintPage[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      /*
       * Rows run top to bottom, because that is the order the sheets come out of a printer
       * and the order somebody laying them on a table expects. The world's y runs the other
       * way, so row 0 takes the *highest* band.
       */
      const maxY = world.maxY - row * sliceHeight;
      pages.push({
        world: boundsOps.bounds(
          world.minX + column * sliceWidth,
          maxY - sliceHeight,
          world.minX + (column + 1) * sliceWidth,
          maxY
        ),
        column,
        row,
      });
    }
  }

  return { pages, pageWidth, pageHeight, margin: setup.margin, scale, columns, rows };
}
